import { isChartKind } from "@/components/global/Chat/chartKind";
import TableMessage from "@/components/global/Chat/components/dreamerCharts/table/TableMessage";
import ChartMessage from "@/components/global/Chat/components/dreamerCharts/hooks/components/ChartMessage";
import { DreamerAttachmentFilterMetadata } from "@/entities/attachments/types";

interface ReportPreviewRendererProps {
  metadata: DreamerAttachmentFilterMetadata;
  /**
   * The message the renderers hand to the schedule modal. A schedule hangs off
   * the originating message, so a fired occurrence's own id would create a
   * second schedule beside the lineage's.
   */
  messageId: string;
  /** The cadence to edit, or null when the report carries none yet. */
  scheduleId: string | null;
  /**
   * The row a first schedule must be written against. Set whenever the preview
   * came from a saved report: without it the modal takes the create path and
   * posts a second save of the same answer, which the server refuses.
   */
  savedReportId: string | null;
  isReplayable?: boolean;
  isExpanded: boolean;
  onClose: () => void;
}

/**
 * A report shown outside the chat, from a list that holds it: mounted hidden so
 * that only the renderer's own expanded modal is on screen.
 *
 * The expansion is controlled rather than left to the renderer, so closing the
 * preview does not unmount it. A dialog torn down while open never runs the
 * cleanup that hands pointer events back, which leaves the whole page
 * unclickable behind it.
 */
export const ReportPreviewRenderer: React.FC<ReportPreviewRendererProps> = ({
  metadata,
  messageId,
  scheduleId,
  savedReportId,
  isReplayable = false,
  isExpanded,
  onClose
}) => {
  const sharedProps = {
    metadata,
    messageId,
    isSchedulable: false,
    isReplayable,
    scheduleId,
    savedReportId,
    isExpanded,
    onExpandedClose: onClose
  };

  return (
    <div className="hidden">
      {isChartKind(metadata.display) ? (
        <ChartMessage {...sharedProps} />
      ) : (
        <TableMessage {...sharedProps} />
      )}
    </div>
  );
};
